import {
  GameCommentModel,
  GameDetailsModel,
  GamePenaltyModel,
  GamePlayerModel,
  HostDetailsModel,
  LocationModel,
  PlayerApplicationModel,
  PlayerInvitationModel
} from '../interfaces';
import { gameLevelMapper } from './game-level.mapper';
import { hostRemovalReasonMapper, playerLeaveReasonMapper } from './leave-reason.mapper';
import { paymentTypesMapper } from './payment-types.mapper';
import { capitalizeFirstLetter, formatDateToString } from '../utils';
import { DateFormats } from '../enums';
import {
  CommonGameDto,
  CommonLocationDto,
  CommonUserPenaltyDto,
  GameDetailsHostDto,
  GameDto,
  GamePlayerApplicationDto,
  GamePlayerDto,
  HostDto,
  HostRemovalReason,
  HostedGameDetailsDto,
  HostedGameInvitationDto,
  HostedGameItemDto,
  HostedGamePlayerApplicationDto,
  HostedGamePlayerDto,
  PaymentTypes,
  PlayerGameDto,
  PlayerLeaveReason,
  PlayerGameInvitationDto,
  UserCommentDto
} from '@core-http';

export const mapCommonGameDto = (game: CommonGameDto): GameDetailsModel => ({
  ...mapBaseGame(game),
  playerApplications: mapPlayerApplications(game.playerApplications)
});

export const mapPlayerGameDto = (game: PlayerGameDto): GameDetailsModel => ({
  ...mapBaseGame(game),
  paymentType: game.paymentTypes?.map((type: PaymentTypes) => paymentTypesMapper[type]),
  gameContactPhone: game.gameContactPhone,
  playerApplications: mapPlayerApplications(game.playerApplications)
});

export const mapPlayerGameInvitationDto = (game: PlayerGameInvitationDto): GameDetailsModel => ({
  ...mapBaseGame(game),
  paymentType: game.paymentTypes?.map((type: PaymentTypes) => paymentTypesMapper[type]),
  gameContactPhone: game.gameContactPhone,
  gameInvitationId: game.gameInvitationId,
  playerApplications: mapPlayerApplications(game.playerApplications)
});

export const mapGameDto = (game: GameDto): GameDetailsModel => ({
  ...mapBaseGame(game),
  paymentType: game.paymentTypes?.map((type: PaymentTypes) => paymentTypesMapper[type]),
  gameContactPhone: game.gameContactPhone,
  players: mapGamePlayers(game.gamePlayers),
  playerApplications: mapPlayerApplications(game.playerApplications),
  isPaid: game.isPaid,
  isHost: game.isCurrentUserHost
});

export const mapHostedGameDetailsDto = (game: HostedGameDetailsDto): GameDetailsModel => ({
  ...mapBaseGame(game),
  paymentType: game.paymentTypes?.map((type: PaymentTypes) => paymentTypesMapper[type]),
  gameContactPhone: game.gameContactPhone,
  players: mapHostedGamePlayers(game.gamePlayers),
  playerApplications: mapHostedGamePlayerApplications(game.playerApplications),
  invitations: mapHostedGameInvitations(game.invitations),
  invitationsRejected: mapHostedGameInvitations(game.invitationsRejected),
  gamePlayersLeft: mapHostedGamePlayers(game.gamePlayersLeft.flat()),
  teamId: game.teamId
});

export const mapHostedGamesItemDto = (game: HostedGameItemDto): GameDetailsModel => ({
  ...mapBaseGame(game),
  paymentType: game.paymentTypes?.map((type: PaymentTypes) => paymentTypesMapper[type]),
  gameContactPhone: game.gameContactPhone,
  playerApplications: mapHostedGamePlayerApplications(game.playerApplications)
});

export const formatDate = (startDate: string, endDate: string): string => {
  const date: string = capitalizeFirstLetter(formatDateToString(startDate, DateFormats.WeekdayFullDate));
  const startTime: string = formatDateToString(startDate, DateFormats.Time);
  const endTime: string = formatDateToString(endDate, DateFormats.Time);
  return `${date} | ${startTime} - ${endTime}`;
};

const mapBaseGame = (
  game: CommonGameDto | PlayerGameDto | PlayerGameInvitationDto | GameDto | HostedGameDetailsDto | HostedGameItemDto
): GameDetailsModel => ({
  gameName: game.name,
  gameId: game.gameId,
  formattedDate: formatDate(game.gameDateStart, game.gameDateEnd),
  gameDateStart: game.gameDateStart,
  duration: getDuration(game.gameDateStart, game.gameDateEnd),
  location: mapLocation(game.location),
  totalSlots: game.totalSlots,
  occupiedSlots: game.occupiedSlots,
  level: gameLevelMapper[game.level],
  priceAmount: +game.priceAmount,
  description: game.description,
  visibility: game.visibility,
  isRecurring: game.isRecurring,
  locationType: game.locationType,
  gameParticipationStatus: game.currentUserParticipationStatus,
  host: mapHost(game.host)
});

export const mapLocation = (location: CommonLocationDto): LocationModel => ({
  locationId: location.locationId,
  name: location.name,
  coordinates: location.coordinates,
  address: location.address,
  city: location.city
});

export const mapHost = (host: HostDto | GameDetailsHostDto): HostDetailsModel => ({
  hostId: host.hostId,
  displayName: host.displayName,
  thumbnail: host.thumbnail,
  lastActive: 'lastActive' in host ? host.lastActive || '' : '',
  isAnonymous: host.isAnonymous,
  rating: {
    hostRating: host.rating.hostRating,
    hostRatingCount: host.rating.hostRatingCount
  }
});

const mapPlayerApplications = (
  applications: GamePlayerApplicationDto[] | undefined
): PlayerApplicationModel[] | undefined => {
  return applications?.map((app: GamePlayerApplicationDto) => ({
    gamePlayerApplicationId: app.gamePlayerApplicationId,
    userId: app.userId,
    userDisplayName: app.userDisplayName,
    userThumbnail: app.userThumbnail,
    playerPreferences: app.playerPreferences
  }));
};

const mapHostedGamePlayerApplications = (applications: HostedGamePlayerApplicationDto[]): PlayerApplicationModel[] => {
  return applications?.map((app: HostedGamePlayerApplicationDto) => ({
    gamePlayerApplicationId: app.gamePlayerApplicationId,
    userId: app.userId,
    userDisplayName: app.userDisplayName,
    userThumbnail: app.userThumbnail,
    penalty: mapCommonPenaltyDto(app.penalty),
    penalties: mapCommonPenaltyDtos(app.penalties),
    comments: mapUserCommentDtos(app.complements),
    playerPreferences: app.playerPreferences
  }));
};

const mapGamePlayers = (players: GamePlayerDto[]): GamePlayerModel[] => {
  return players?.map((player: GamePlayerDto) => ({
    playerId: player.playerId,
    userId: player.userId,
    userDisplayName: player.userDisplayName,
    userThumbnail: resolvePlayerThumbnail(player.userId, player.userThumbnail),
    isHost: player.isHost,
    playerPreferences: player.playerPreferences
  }));
};

const mapHostedGamePlayers = (players: HostedGamePlayerDto[]): GamePlayerModel[] => {
  return players?.map((player: HostedGamePlayerDto) => ({
    playerId: player.playerId,
    userId: player.userId,
    userDisplayName: player.userDisplayName,
    isPaid: player.isPaid,
    userThumbnail: resolvePlayerThumbnail(player.userId, player.userThumbnail),
    isHost: player.isHost,
    penalty: mapCommonPenaltyDto(player.penalty),
    penalties: mapCommonPenaltyDtos(player.penalties),
    comments: mapUserCommentDtos(player.complements),
    playerPreferences: player.playerPreferences,
    leaveReasonLabel: mapLeaveReasonLabel(player)
  }));
};

const mapLeaveReasonLabel = (player: HostedGamePlayerDto): string | undefined => {
  if (player.leaveReason) {
    if (player.leaveReason === PlayerLeaveReason.PREFER_NOT_TO_SAY) return undefined;

    const reason: string | undefined =
      player.leaveReason === PlayerLeaveReason.OTHER
        ? player.reasonText?.trim() || undefined
        : playerLeaveReasonMapper[player.leaveReason];

    return reason && `Powód: ${reason}`;
  }

  if (player.hostRemovalReason) {
    if (player.hostRemovalReason === HostRemovalReason.PREFER_NOT_TO_SAY) return undefined;

    const reason: string | undefined =
      player.hostRemovalReason === HostRemovalReason.OTHER
        ? player.reasonText?.trim() || undefined
        : hostRemovalReasonMapper[player.hostRemovalReason];

    return reason && `Usunąłeś z powodu: ${reason}`;
  }

  return undefined;
};

const mapHostedGameInvitations = (invitations: HostedGameInvitationDto[][]): PlayerInvitationModel[] => {
  const flatInvitations: HostedGameInvitationDto[] = invitations.flat();

  return flatInvitations?.map((invitation: HostedGameInvitationDto) => ({
    gameInvitationId: invitation.gameInvitationId,
    penalty: mapCommonPenaltyDto(invitation.penalty),
    penalties: mapCommonPenaltyDtos(invitation.penalties),
    comments: mapUserCommentDtos(invitation.complements),
    userDisplayName: invitation.userDisplayName,
    userId: invitation.userId,
    userThumbnail: invitation.userThumbnail
  }));
};

const mapCommonPenaltyDto = (penalty: CommonUserPenaltyDto | null): GamePenaltyModel | null => {
  if (!penalty) return null;
  return {
    penaltyReason: penalty.penaltyReason,
    penaltyType: penalty.penaltyType,
    userPenaltyId: penalty.userPenaltyId ?? undefined,
    revokedAt: penalty.revokedAt,
    gameId: penalty.gameId,
    canRevoke: penalty.canRevoke
  };
};

const mapCommonPenaltyDtos = (penalties: CommonUserPenaltyDto[] | undefined): GamePenaltyModel[] =>
  (penalties ?? []).map((penalty: CommonUserPenaltyDto) => mapCommonPenaltyDto(penalty)!);

const mapUserCommentDtos = (comments: UserCommentDto[] | undefined): GameCommentModel[] =>
  (comments ?? []).map((comment: UserCommentDto) => ({
    commentId: comment.commentId,
    commentType: comment.commentType,
    createdAt: comment.createdAt,
    canRevoke: comment.canRevoke
  }));

const getDuration = (startDate: string, endDate: string): number => {
  const start: Date = new Date(startDate);
  const end: Date = new Date(endDate);
  const differenceInMilliseconds: number = end.getTime() - start.getTime();
  return differenceInMilliseconds / (1000 * 60);
};

export const resolvePlayerThumbnail = (userId: string | null, userThumbnail: string | null): string | null => {
  const MANUAL_PLAYER_AVATAR: string = 'assets/icons/manual-player-avatar.svg';

  return userId ? userThumbnail : MANUAL_PLAYER_AVATAR;
};
